// src/components/TagCategoryDialog.tsx
import React, { useState } from 'react';
import {
  Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Stack,
  FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';

import type { TagCategory } from '../types/position.ts';
import { createTagCategory, updateTagCategory } from '../utils/tagsApi.ts';

interface Props {
  open: boolean;
  onClose: () => void;
  editing: TagCategory | null;
  isAdmin: boolean;
  scopeUserId: number | null;
  availableGlobalsForShadow: TagCategory[];
  onSaved: () => Promise<void>;
  onError: (msg: string) => void;
}

const TagCategoryDialog: React.FC<Props> = ({ open, onClose, editing, isAdmin, scopeUserId, availableGlobalsForShadow, onSaved, onError }) => {
  const [name, setName] = useState(editing?.tag_category ?? '');
  const [scope, setScope] = useState<'user' | 'global'>(editing?.scope ?? 'user');
  const [shadowsId, setShadowsId] = useState<number | null>(editing?.shadows_category_id ?? null);

  React.useEffect(() => {
    setName(editing?.tag_category ?? '');
    setScope(editing?.scope ?? 'user');
    setShadowsId(editing?.shadows_category_id ?? null);
  }, [editing, open]);

  const handleSubmit = async () => {
    try {
      if (editing) {
        const body: { tag_category?: string; shadows_category_id?: number | null } = {};
        if (name !== editing.tag_category) body.tag_category = name;
        if (editing.scope === 'user') body.shadows_category_id = shadowsId;
        await updateTagCategory(editing.id, body, scopeUserId);
      } else {
        await createTagCategory(
          { scope, tag_category: name, shadows_category_id: scope === 'user' ? shadowsId : null },
          scopeUserId,
        );
      }
      await onSaved();
    } catch (err) { onError((err as Error).message); }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{editing ? 'Edit Category' : 'New Category'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} fullWidth />
          {!editing && (
            <FormControl fullWidth>
              <InputLabel>Scope</InputLabel>
              <Select label="Scope" value={scope} onChange={(e) => setScope(e.target.value as any)}>
                <MenuItem value="user">Personal</MenuItem>
                {isAdmin && <MenuItem value="global">Global</MenuItem>}
              </Select>
            </FormControl>
          )}
          {scope === 'user' && (
            <FormControl fullWidth>
              <InputLabel>Shadows Global</InputLabel>
              <Select label="Shadows Global" value={shadowsId ?? ''}
                onChange={(e) => setShadowsId(e.target.value === '' ? null : Number(e.target.value))}>
                <MenuItem value="">— None —</MenuItem>
                {availableGlobalsForShadow.map(g => (
                  <MenuItem key={g.id} value={g.id}>{g.tag_category}</MenuItem>
                ))}
                {editing?.shadows_category_id && !availableGlobalsForShadow.some(g => g.id === editing.shadows_category_id) && (
                  <MenuItem value={editing.shadows_category_id}>(Current)</MenuItem>
                )}
              </Select>
            </FormControl>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!name.trim()}>Save</Button>
      </DialogActions>
    </Dialog>
  );
};

export default TagCategoryDialog;
